import { z } from 'zod';
import { LENDER_NAME_INVALID, SALESFORCE_ID_INVALID, NMLS_ID_INVALID, ADDRESS_REQUIRED, ZIP_INVALID } from '../copy';
import { US_STATE_CODES } from '../usStates';

export const SALESFORCE_ID_LENGTH = 18;

const required = (message: string) => z.string().trim().min(1, message);

export const accountSchema = z.object({
  lenderName: z.string().regex(/[a-zA-Z0-9]/, LENDER_NAME_INVALID),
  // The account's code — it becomes the urn. Suggested from the name, editable, constrained to
  // the urn-safe charset every existing lender name already uses.
  salesforceId: z
    .string()
    .refine(
      (v) => v === '' || new RegExp(`^[a-zA-Z0-9]{${SALESFORCE_ID_LENGTH}}$`).test(v),
      SALESFORCE_ID_INVALID,
    ),
  // Optional, and validated only for numeric characters: NMLS identifiers vary in length.
  nmlsId: z.string().refine((v) => v === '' || /^\d+$/.test(v), NMLS_ID_INVALID),
  // All four are required — "Next" stays inactive until every one of them is filled.
  street: required(ADDRESS_REQUIRED),
  city: required(ADDRESS_REQUIRED),
  state: z.string().refine((v) => US_STATE_CODES.includes(v), ADDRESS_REQUIRED),
  // Exactly five digits, checked on the string so a leading zero survives. The length matters as
  // much as the charset: a digits-only rule let a 23-digit zip through UAT (OM-4362). Empty is
  // left to the required rule, so a blank field reads "Required" rather than a format error.
  zip: required(ADDRESS_REQUIRED).refine((v) => v === '' || /^\d{5}$/.test(v), ZIP_INVALID),
  entityId: z.string(),
  entityName: z.string(),
  apiUsername: z.string(),
  apiPassword: z.string(),
});

export type AccountFormValues = z.infer<typeof accountSchema>;
